import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, FileDown, Loader2, Lock, MessageSquarePlus, Unlock } from 'lucide-react';
import { haccpApi, erroreHaccp, scaricaPdf, dataIt } from '../haccpApi';
import { BadgeLivello } from './BadgeRichiamo';
import { TestoDialog } from './TestoDialog';

const Info = ({ r }) => {
  const righe = [['Marca', r.marca], ['Lotti', (r.lotti || []).join(', ')], ['Scadenze', (r.scadenze || []).join(', ')], ['Fornitore', r.fornitore],
    ['Produttore', r.produttore], ['Motivo', r.motivo], ['Aperto il', `${dataIt(r.aperto_il)} da ${r.creato_da?.nome || ''}`]];
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 grid sm:grid-cols-2 gap-x-4 gap-y-1.5">
      {righe.filter(([, v]) => v).map(([k, v]) => <p key={k} className="text-sm text-gray-800"><span className="text-gray-500">{k}: </span>{v}</p>)}
      {r.esito && <p className="text-sm text-gray-800 sm:col-span-2" data-testid="richiamo-esito"><span className="text-gray-500">Esito: </span>{r.esito}</p>}
    </div>
  );
};

const Preparazioni = ({ r, puo, onSblocca }) => (
  <div className="space-y-2">
    <h4 className="text-sm font-bold text-gray-900">Preparazioni bloccate</h4>
    {!r.preparazioni.length && <p className="text-sm text-gray-600">Nessuna.</p>}
    {r.preparazioni.map((p) => (
      <div key={p.lotto_id} className={`rounded-xl border p-3 flex items-start gap-3 ${p.tolto ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'}`} data-testid={`richiamo-bloccata-${p.lotto_id}`}>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${p.tolto ? 'text-gray-500 line-through' : 'text-red-900'}`}>{p.nome} · lotto {p.numero_lotto}</p>
          <p className="text-xs text-gray-600">Prodotta il {dataIt(p.data)} · {p.ingrediente}</p>
          {p.tolto && <p className="text-xs text-gray-600 mt-1">Sbloccata da {p.tolto.nome}: {p.tolto.motivo}</p>}
        </div>
        {puo && !p.tolto && (
          <button onClick={() => onSblocca(p)} data-testid={`richiamo-sblocca-${p.lotto_id}`}
            className="h-9 px-3 rounded-lg border border-gray-300 bg-white text-xs font-medium text-gray-700 flex items-center gap-1"><Unlock className="h-3.5 w-3.5" />Sblocca</button>
        )}
      </div>
    ))}
  </div>
);

const Merce = ({ merce }) => (
  <div className="space-y-2">
    <h4 className="text-sm font-bold text-gray-900">Acquisti collegati</h4>
    {!merce.length && <p className="text-sm text-gray-600">Nessuno.</p>}
    {merce.map((m) => (
      <div key={m.chiave} className="rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex items-center gap-2"><BadgeLivello livello={m.livello} /><p className="text-sm font-semibold text-gray-900 break-words">{m.prodotto}</p></div>
        <p className="text-xs text-gray-600 mt-1">{m.documento} del {dataIt(m.data)} · {m.fornitore || '-'}{m.lotto ? ` · lotto ${m.lotto}` : ''}</p>
      </div>
    ))}
  </div>
);

const Storico = ({ storico }) => (
  <div className="space-y-1.5" data-testid="richiamo-storico">
    <h4 className="text-sm font-bold text-gray-900">Azioni registrate</h4>
    {storico.map((s, i) => (
      <p key={i} className="text-xs text-gray-700 bg-white border border-gray-200 rounded-lg p-2">
        <span className="font-semibold">{dataIt(s.data_ora)} {String(s.data_ora).slice(11, 16)} · {s.nome}</span> - {s.azione}{s.testo ? `: ${s.testo}` : ''}
      </p>
    ))}
  </div>
);

export const RichiamoDettaglio = ({ id, responsabile, onIndietro, onCambiato }) => {
  const [r, setR] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [pdf, setPdf] = useState(false);
  const carica = useCallback(async () => {
    try { setR((await haccpApi.get(`/richiami/${id}`)).richiamo); } catch (e) { setR(false); toast.error(erroreHaccp(e, 'Richiamo non trovato')); }
  }, [id]);
  useEffect(() => { carica(); }, [carica]);

  const azione = async (path, testo, messaggio) => {
    setR((await haccpApi.post(`/richiami/${id}${path}`, { testo })).richiamo);
    toast.success(messaggio);
    onCambiato();
  };
  const scarica = async () => {
    setPdf(true);
    try { await scaricaPdf(`/richiami/${id}/pdf`, `Richiamo_${r.numero.replace('/', '-')}.pdf`); }
    catch (e) { toast.error(erroreHaccp(e, 'PDF non disponibile')); } finally { setPdf(false); }
  };

  if (r === false) {
    return (
      <div className="space-y-3" data-testid="richiamo-errore">
        <p className="text-sm text-gray-700">Non riesco ad aprire il richiamo.</p>
        <div className="flex gap-2">
          <button onClick={carica} className="h-10 px-4 rounded-full bg-gray-900 text-white text-sm" data-testid="richiamo-riprova">Riprova</button>
          <button onClick={onIndietro} className="h-10 px-4 rounded-full border border-gray-300 bg-white text-sm text-gray-800">Richiami</button>
        </div>
      </div>
    );
  }
  if (!r) return <div className="p-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  const puo = responsabile && r.stato === 'aperto';
  return (
    <div className="space-y-4" data-testid="richiamo-dettaglio">
      <button onClick={onIndietro} className="flex items-center gap-1 text-sm text-gray-600" data-testid="richiamo-dettaglio-indietro"><ArrowLeft className="h-4 w-4" />Richiami</button>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-gray-500">Richiamo n. {r.numero}</p>
          <h3 className="text-lg font-bold text-gray-900" data-testid="richiamo-titolo">{r.prodotto || '-'}</h3>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${r.stato === 'aperto' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700'}`} data-testid="richiamo-stato">
          {r.stato === 'aperto' ? 'APERTO' : `CHIUSO il ${dataIt(r.chiuso_il)}`}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={scarica} disabled={pdf} data-testid="richiamo-pdf-btn" className="h-10 px-4 rounded-full bg-gray-900 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-60">
          {pdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}Registro PDF
        </button>
        {puo && <button onClick={() => setDialog({ tipo: 'nota' })} data-testid="richiamo-nota-btn" className="h-10 px-4 rounded-full border border-gray-300 bg-white text-sm font-medium text-gray-800 flex items-center gap-2"><MessageSquarePlus className="h-4 w-4" />Aggiungi nota</button>}
        {puo && <button onClick={() => setDialog({ tipo: 'chiudi' })} data-testid="richiamo-chiudi-btn" className="h-10 px-4 rounded-full border border-red-300 bg-white text-sm font-medium text-red-700 flex items-center gap-2"><Lock className="h-4 w-4" />Chiudi richiamo</button>}
      </div>
      <Info r={r} />
      <Preparazioni r={r} puo={puo} onSblocca={(p) => setDialog({ tipo: 'sblocca', p })} />
      <Merce merce={r.merce} />
      <Storico storico={r.storico} />
      <TestoDialog open={dialog?.tipo === 'nota'} titolo="Aggiungi una nota" descrizione="Es. fornitore avvisato, merce messa da parte in cella."
        segnaposto="Cosa hai fatto" bottone="Salva nota" testid="richiamo-nota" onClose={() => setDialog(null)}
        onConferma={(t) => azione('/nota', t, 'Nota salvata')} />
      <TestoDialog open={dialog?.tipo === 'chiudi'} titolo="Chiudi il richiamo" descrizione="Scrivi com'è finita: le preparazioni restano segnate come richiamate."
        segnaposto="es. 6 confezioni rese al fornitore il 12/11, 2 kg di tiramisù buttati" bottone="Chiudi richiamo" testid="richiamo-chiudi"
        onClose={() => setDialog(null)} onConferma={(t) => azione('/chiudi', t, 'Richiamo chiuso')} />
      <TestoDialog open={dialog?.tipo === 'sblocca'} titolo={`Sblocca il lotto ${dialog?.p?.numero_lotto || ''}`}
        descrizione="Solo se la preparazione non contiene il prodotto richiamato: il motivo resta nel registro." segnaposto="es. fatta con un'altra marca"
        bottone="Sblocca" testid="richiamo-sblocca" onClose={() => setDialog(null)}
        onConferma={(t) => azione(`/togli/${dialog.p.lotto_id}`, t, 'Preparazione sbloccata')} />
    </div>
  );
};
